/**
 * The ONE list of tabs the agent's left panel carries — Jobs, Tables,
 * Variables, DataViz, Skills — built here for every page that draws the panel: the
 * start screen, the session page and the design showcase. The same list feeds
 * the <SidePanelLauncher> dial, which is why each tab carries an icon. There used to be a
 * list per page, and they drifted (the start screen had no DataViz tab; Adam,
 * 11 Sep: "can we just use the same one?").
 *
 * The pages differ only in where the session-side values come from — the
 * session page reads them from the chat context, the start screen from its
 * own state — so a page hands in a hook, `useValues`, and each tab's body
 * calls it. A hook rather than plain values because the session page's
 * values live in a context that only exists INSIDE the panel's tree.
 */
import React from 'react'
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined'
import ExtensionOutlinedIcon from '@mui/icons-material/ExtensionOutlined'
import InsertChartOutlinedIcon from '@mui/icons-material/InsertChartOutlined'
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined'
import WorkOutlineIcon from '@mui/icons-material/WorkOutline'
import type { AgentSidePanelTab, HoistedSkillInfo } from '../../agent'
import AgentJobsTab from './AgentJobsTab'
import AgentTocTab from './AgentTocTab'
import AgentVariablesTab from './AgentVariablesTab'
import AgentDashboardsTab from './AgentDashboardsTab'
import AgentSkillsTab from './AgentSkillsTab'
import type { ComposerChip } from '../../hooks/useComposerChips'

export interface PanelSessionValues {
  /** Empty before a session exists: the Jobs tab's tick is then the selection itself. */
  sessionId: string
  /** The Jobs tab's live selection; null until the first event (session page). */
  sessionJobs: string[] | null
  /** What the Jobs tab shows until an event arrives. */
  fallbackJobs: string[]
  onJobsChanged?: (jobs: string[]) => void
  /** The job the Tables and Variables trees scope to. */
  job: string
  /** The job set the trees' pickers offer. */
  treeJobs: string[]
  /** Sends a message into the chat (the Skills tab's Distil and Install). Absent before a chat exists. */
  onSendMessage?: (message: string) => void
  /** Skills the chat installed live (skill_installed). */
  installedSkillNames?: Set<string>
  /** The chat's last distilled skill (skill_hoisted): the Skills tab opens its draft. */
  hoistedSkill?: HoistedSkillInfo | null
  /** The chat holds a finished agent turn (Distil is disabled until then). */
  hasCompletedTurn?: boolean
}

export interface AgentSidePanelTabsInput {
  customer: string
  chipIds: Set<string>
  onToggle: (chip: ComposerChip, on: boolean) => void
  onPreview?: (chip: ComposerChip) => void
  /** Called inside the panel, on every render of a tab body. */
  useValues: () => PanelSessionValues
}

/** Calls the page's hook where hooks may be called, then renders the tab. */
function Scoped({ useValues, children }: { useValues: () => PanelSessionValues; children: (v: PanelSessionValues) => React.ReactElement }) {
  return children(useValues())
}

export function buildAgentSidePanelTabs({ customer, chipIds, onToggle, onPreview, useValues }: AgentSidePanelTabsInput): AgentSidePanelTab[] {
  return [
    {
      key: 'jobs',
      label: 'Jobs',
      icon: <WorkOutlineIcon fontSize="small" />,
      render: () => (
        <Scoped useValues={useValues}>
          {v => <AgentJobsTab customer={customer} sessionId={v.sessionId} sessionJobs={v.sessionJobs} fallbackJobs={v.fallbackJobs} onJobsChanged={v.onJobsChanged} />}
        </Scoped>
      ),
    },
    {
      key: 'toc',
      label: 'Tables',
      icon: <TableChartOutlinedIcon fontSize="small" />,
      render: () => (
        <Scoped useValues={useValues}>
          {v => <AgentTocTab customer={customer} job={v.job} sessionJobs={v.treeJobs} selectedIds={chipIds} onToggle={onToggle} onPreview={onPreview} />}
        </Scoped>
      ),
    },
    {
      key: 'variables',
      label: 'Variables',
      icon: <AccountTreeOutlinedIcon fontSize="small" />,
      render: () => (
        <Scoped useValues={useValues}>
          {v => <AgentVariablesTab customer={customer} job={v.job} sessionJobs={v.treeJobs} selectedIds={chipIds} onToggle={onToggle} onPreview={onPreview} />}
        </Scoped>
      ),
    },
    {
      // Every dashboard the customer has, in the DataViz page's three tiers;
      // a tick attaches one for the agent to read (Adam, 11 Sep).
      key: 'dashboards',
      label: 'DataViz',
      icon: <InsertChartOutlinedIcon fontSize="small" />,
      render: () => (
        <AgentDashboardsTab customer={customer} selectedIds={chipIds} onToggle={onToggle} onPreview={onPreview} />
      ),
    },
    {
      // The customer's skills in three tiers; distil this chat into a draft,
      // edit, test and save (B2). Replaces the Session options Skill Library.
      key: 'skills',
      label: 'Skills',
      icon: <ExtensionOutlinedIcon fontSize="small" />,
      render: () => (
        <Scoped useValues={useValues}>
          {v => (
            <AgentSkillsTab
              customer={customer}
              sessionId={v.sessionId}
              job={v.job}
              jobs={v.treeJobs}
              installedSkillNames={v.installedSkillNames}
              hoistedSkill={v.hoistedSkill}
              onSendMessage={v.onSendMessage}
              hasCompletedTurn={v.hasCompletedTurn ?? false}
            />
          )}
        </Scoped>
      ),
    },
  ]
}
